import React from 'react';
import {
  Box, Typography, Skeleton, alpha, Tooltip, Chip,
  Table, TableBody, TableCell, TableHead, TableRow,
} from '@mui/material';
import {
  Shield, Visibility, RemoveCircleOutline, SellOutlined,
  Assessment, FilterList,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import html2canvas from 'html2canvas';

export const fmtPct = (n, d = 1) => n != null ? `${n >= 0 ? '+' : ''}${(n * 100).toFixed(d)}%` : '—';
export const fmtScore = (n) => n != null ? n.toFixed(1) : '—';
export const fmtBn = (n) => n != null ? `${(n / 1e9).toFixed(1)}B` : '—';
export const fmtSignedPct = (n, d = 1) => n != null ? `${(n * 100).toFixed(d)}%` : '—';

export const thSx = {
  fontSize: '0.72rem', fontWeight: 700, color: '#94a3b8',
  letterSpacing: '0.02em', py: 0.8,
  borderBottom: '1px solid #e2e8f0', bgcolor: '#fafbfc',
};

export const tdSx = { py: 0.7, fontSize: '0.8rem', borderBottom: '1px solid #f1f5f9' };

export const LEVEL_META = {
  1: { key: 'shaking', action: 'HOLD', color: '#10b981', icon: Shield },
  2: { key: 'profitTaking', action: 'MONITOR', color: '#f59e0b', icon: Visibility },
  3: { key: 'distributing', action: 'REDUCE', color: '#f97316', icon: RemoveCircleOutline },
  4: { key: 'trendBreak', action: 'SELL', color: '#ef4444', icon: SellOutlined },
};

export const REGIME_COLORS = {
  risk_on: '#10b981', neutral: '#64748b', warning: '#f59e0b',
  profit_taking: '#f97316', distribution: '#ef4444', strong_distribution: '#dc2626',
  risk_off: '#991b1b', washout_recovery: '#06b6d4',
};

export const levelColor = (level) => LEVEL_META[level]?.color || '#94a3b8';

export const actionColor = (action) => {
  const mapping = { HOLD: '#10b981', MONITOR: '#f59e0b', REDUCE: '#f97316', SELL: '#ef4444' };
  return mapping[action] || '#94a3b8';
};

const SIGNAL_KEYS = [
  'sig_distribution_day', 'sig_upper_wick_cluster', 'sig_weak_bounce',
  'sig_ma10_breakdown', 'sig_ma20_breakdown', 'sig_vol_price_divergence',
  'sig_relative_weakness', 'sig_close_at_low', 'sig_dist_count_high',
];

const PATTERN_KEYS = [
  'pat_upthrust', 'pat_climax', 'pat_weak_lower_high', 'pat_box_break',
];

export const LEVEL_FILTERS = [
  { key: 'all', i18n: 'distribution.filters.all', color: '#64748b', icon: FilterList },
  { key: 'SELL', i18n: 'distribution.filters.trendBreak', color: '#ef4444', icon: SellOutlined },
  { key: 'REDUCE', i18n: 'distribution.filters.distributing', color: '#f97316', icon: RemoveCircleOutline },
  { key: 'MONITOR', i18n: 'distribution.filters.profitTaking', color: '#f59e0b', icon: Visibility },
  { key: 'HOLD', i18n: 'distribution.filters.shaking', color: '#10b981', icon: Shield },
];

export const getActiveItems = (row, t) => {
  const active = [];
  SIGNAL_KEYS.forEach((key) => {
    if (row[key] === 1) active.push({ key, label: t(`distribution.signals.${key}`), type: 'signal' });
  });
  PATTERN_KEYS.forEach((key) => {
    if (row[key] === 1) active.push({ key, label: t(`distribution.patterns.${key}`), type: 'pattern' });
  });
  return active;
};

export const getMaSummary = (row, t) => {
  const labels = ['10', '20', '50'];
  const lost = labels.filter((label) => !row[`above_ma${label}`]);

  if (lost.length === 0) {
    return { label: t('distribution.table.aboveMa', { labels: labels.join('/') }), color: '#10b981' };
  }

  return { label: t('distribution.table.lostMa', { labels: lost.join('/') }), color: '#ef4444' };
};

export const getDistributionSummary = (count, t) => {
  if (!count) return t('distribution.table.noDistSessions');
  return t('distribution.table.distSessions', { count });
};

export const getVolumeSummary = (volRatio, t) => {
  if (volRatio == null) return { label: t('distribution.table.flowNeutral'), color: '#94a3b8' };
  if (volRatio >= 1.8) return { label: t('distribution.table.flowVeryHigh'), color: '#ef4444' };
  if (volRatio >= 1.15) return { label: t('distribution.table.flowHigh'), color: '#f97316' };
  if (volRatio >= 0.8) return { label: t('distribution.table.flowNormal'), color: '#475569' };
  return { label: t('distribution.table.flowLow'), color: '#94a3b8' };
};

export const getCloseSummary = (closePos, t) => {
  if (closePos == null) return t('distribution.table.closeNeutral');
  if (closePos >= 0.8) return t('distribution.table.closeHigh');
  if (closePos >= 0.55) return t('distribution.table.closeUpper');
  if (closePos >= 0.3) return t('distribution.table.closeLower');
  return t('distribution.table.closeLow');
};

export const getStrengthSummary = (rs5, t) => {
  if (rs5 == null) return { label: t('distribution.table.strengthNeutral'), color: '#94a3b8' };
  if (rs5 >= 0.03) return { label: t('distribution.table.strengthGood'), color: '#10b981' };
  if (rs5 > -0.03) return { label: t('distribution.table.strengthFlat'), color: '#64748b' };
  return { label: t('distribution.table.strengthWeak'), color: '#ef4444' };
};

export const getDrawerActionSummary = (latest, sectorCtx, t) => {
  if (!latest) return null;

  const levelMeta = LEVEL_META[latest.dist_level] || LEVEL_META[1];
  const action = latest.dist_action || levelMeta.action;
  const activeItems = getActiveItems(latest, t);
  const drivers = activeItems.slice(0, 3).map((item) => item.label);

  const reasons = [];
  if (drivers.length > 0) {
    reasons.push(drivers.slice(0, 2).join(' • '));
  }
  if ((latest.dist_count || 0) >= 4) {
    reasons.push(t('distribution.drawer.summary.distCountNote', { count: latest.dist_count }));
  } else if (latest.ret_10 != null) {
    reasons.push(t('distribution.drawer.summary.ret10Note', { value: fmtPct(latest.ret_10) }));
  } else if (latest.rs_5 != null || latest.close_pos != null) {
    reasons.push(t('distribution.drawer.summary.strengthNote', {
      rs: fmtPct(latest.rs_5),
      close: getCloseSummary(latest.close_pos, t),
    }));
  }

  let sectorNote = t('distribution.drawer.summary.sectorNeutral');
  if (sectorCtx?.sector_action_hint) {
    sectorNote = `${t('distribution.drawer.summary.sectorHintPrefix')} ${sectorCtx.sector_action_hint}.`;
  } else if (sectorCtx?.sector_regime) {
    if (['risk_off', 'distribution', 'strong_distribution'].includes(sectorCtx.sector_regime)) {
      sectorNote = t('distribution.drawer.summary.sectorHeadwind');
    } else if (['risk_on', 'washout_recovery'].includes(sectorCtx.sector_regime)) {
      sectorNote = t('distribution.drawer.summary.sectorTailwind');
    }
  }

  return {
    headline: t(`distribution.drawer.summary.actionLead.${action}`),
    rationale: reasons.filter(Boolean).slice(0, 2).join(' | '),
    drivers,
    sectorNote,
    color: actionColor(action),
    actionLabel: t(`distribution.actions.${action}`, action),
  };
};

export const getSectorLabel = (item, lang) => {
  if (!item) return '';
  return item.sector_name?.[lang] || item.sector_name?.en || item.sector_name_vi || item.sector_name_en || item.sector_code || item.sector || '';
};

export const getTimelineTicks = (points, maxTicks = 6) => {
  if (!points.length) return [];
  if (points.length <= maxTicks) return points.map((point) => point.x);

  const step = Math.max(1, Math.ceil((points.length - 1) / (maxTicks - 1)));
  const ticks = points.filter((_, index) => index % step === 0).map((point) => point.x);
  const last = points[points.length - 1].x;

  if (ticks[ticks.length - 1] !== last) ticks.push(last);
  return ticks;
};

export const getTimelineDomain = (points) => {
  if (!points.length) return [0, 0];
  return [-0.2, points.length - 1 + 0.2];
};

export const exportDrawerCapture = async ({ captureNode, scrollNode, fileName, backgroundColor = '#fafbfc' }) => {
  if (!captureNode || !scrollNode) return;

  const paperNode = captureNode.parentElement;
  const previousOverflow = scrollNode.style.overflowY;
  const previousHeight = scrollNode.style.height;
  const previousMaxHeight = scrollNode.style.maxHeight;
  const previousFlex = scrollNode.style.flex;
  const previousCaptureHeight = captureNode.style.height;
  const previousPaperHeight = paperNode?.style.height || '';
  const previousPaperMaxHeight = paperNode?.style.maxHeight || '';
  const previousPaperOverflow = paperNode?.style.overflow || '';
  const previousPaperOverflowY = paperNode?.style.overflowY || '';
  const headerToolbarState = [];

  try {
    if (paperNode) {
      paperNode.style.height = 'auto';
      paperNode.style.maxHeight = 'none';
      paperNode.style.overflow = 'visible';
      paperNode.style.overflowY = 'visible';
    }

    scrollNode.style.overflowY = 'visible';
    scrollNode.style.height = 'auto';
    scrollNode.style.maxHeight = 'none';
    scrollNode.style.flex = '0 0 auto';
    captureNode.style.height = `${captureNode.scrollHeight}px`;

    captureNode.querySelectorAll('[data-export-header-toolbar="true"]').forEach((node) => {
      if (!(node instanceof HTMLElement)) return;
      headerToolbarState.push({
        node,
        hadIgnore: node.hasAttribute('data-html2canvas-ignore'),
        previousVisibility: node.style.visibility,
        previousPointerEvents: node.style.pointerEvents,
      });
      node.removeAttribute('data-html2canvas-ignore');
      node.style.visibility = 'hidden';
      node.style.pointerEvents = 'none';
    });

    await new Promise((resolve) => window.requestAnimationFrame(resolve));

    const canvas = await html2canvas(captureNode, {
      backgroundColor,
      scale: 2,
      useCORS: true,
      width: captureNode.scrollWidth,
      height: captureNode.scrollHeight,
      windowWidth: captureNode.scrollWidth,
      windowHeight: captureNode.scrollHeight,
      scrollX: 0,
      scrollY: 0,
    });

    const link = document.createElement('a');
    link.download = fileName;
    link.href = canvas.toDataURL('image/png');
    link.click();
  } finally {
    scrollNode.style.overflowY = previousOverflow;
    scrollNode.style.height = previousHeight;
    scrollNode.style.maxHeight = previousMaxHeight;
    scrollNode.style.flex = previousFlex;
    captureNode.style.height = previousCaptureHeight;

    if (paperNode) {
      paperNode.style.height = previousPaperHeight;
      paperNode.style.maxHeight = previousPaperMaxHeight;
      paperNode.style.overflow = previousPaperOverflow;
      paperNode.style.overflowY = previousPaperOverflowY;
    }

    headerToolbarState.forEach(({ node, hadIgnore, previousVisibility, previousPointerEvents }) => {
      if (hadIgnore) node.setAttribute('data-html2canvas-ignore', 'true');
      else node.removeAttribute('data-html2canvas-ignore');
      node.style.visibility = previousVisibility;
      node.style.pointerEvents = previousPointerEvents;
    });
  }
};

export const Tile = ({ label, value, sub, color, Icon }) => (
  <Box sx={{
    position: 'relative', overflow: 'hidden',
    borderRadius: 1.5, border: `1px solid ${alpha(color, 0.15)}`,
    bgcolor: '#fff', p: '14px 16px', transition: 'all 0.2s',
    '&:hover': { borderColor: alpha(color, 0.3), boxShadow: `0 4px 16px ${alpha(color, 0.08)}`, transform: 'translateY(-1px)' },
  }}>
    <Box sx={{ position: 'absolute', top: 0, right: 0, width: 64, height: 64, background: `radial-gradient(circle at top right, ${alpha(color, 0.06)} 0%, transparent 70%)` }} />
    <Box sx={{ position: 'relative', display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
      <Box sx={{ width: 38, height: 38, borderRadius: 2, flexShrink: 0, background: `linear-gradient(135deg, ${color} 0%, ${alpha(color, 0.7)} 100%)`, display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: `0 3px 8px ${alpha(color, 0.25)}` }}>
        <Icon sx={{ fontSize: 20, color: '#fff' }} />
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', color: '#94a3b8', lineHeight: 1, mb: 0.5 }}>{label}</Typography>
        <Typography sx={{ fontSize: '1.75rem', fontWeight: 900, color, lineHeight: 1.2, fontFamily: 'monospace' }}>{value}</Typography>
        {sub && <Typography sx={{ fontSize: '0.68rem', color: '#94a3b8', mt: 0.4 }}>{sub}</Typography>}
      </Box>
    </Box>
  </Box>
);

export const LevelBadge = ({ level }) => {
  const { t } = useTranslation();
  const meta = LEVEL_META[level] || LEVEL_META[1];
  return (
    <Tooltip title={t(`distribution.levelDesc.${meta.key}`)} arrow>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <meta.icon sx={{ fontSize: 14, color: meta.color }} />
        <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: meta.color }}>
          {t(`distribution.levels.${meta.key}`)}
        </Typography>
      </Box>
    </Tooltip>
  );
};

export const SignalChips = ({ row }) => {
  const { t } = useTranslation();
  const active = getActiveItems(row, t);

  if (active.length === 0) return <Typography sx={{ fontSize: '0.7rem', color: '#cbd5e1' }}>—</Typography>;

  return (
    <Box sx={{ display: 'flex', gap: 0.4, flexWrap: 'wrap' }}>
      {active.map(({ key, label, type }) => (
        <Chip
          key={key}
          label={label}
          size="small"
          sx={{
            fontSize: '0.58rem', height: 18, fontWeight: 600,
            bgcolor: type === 'pattern' ? alpha('#7c3aed', 0.08) : alpha('#3b82f6', 0.08),
            color: type === 'pattern' ? '#7c3aed' : '#3b82f6',
          }}
        />
      ))}
    </Box>
  );
};

export const SignalHighlights = ({ row }) => {
  const { t } = useTranslation();
  const active = getActiveItems(row, t);

  if (active.length === 0) {
    return <Typography sx={{ fontSize: '0.66rem', color: '#cbd5e1' }}>—</Typography>;
  }

  const visible = active.slice(0, 2);
  const remaining = active.length - visible.length;

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.35, flexWrap: 'wrap' }}>
      {visible.map(({ key, label, type }) => (
        <Chip
          key={key}
          label={label}
          size="small"
          sx={{
            height: 18,
            fontSize: '0.56rem',
            fontWeight: 700,
            bgcolor: type === 'pattern' ? alpha('#7c3aed', 0.08) : alpha('#3b82f6', 0.08),
            color: type === 'pattern' ? '#7c3aed' : '#3b82f6',
          }}
        />
      ))}
      {remaining > 0 && (
        <Typography sx={{ fontSize: '0.58rem', color: '#94a3b8', fontWeight: 700 }}>
          +{remaining}
        </Typography>
      )}
    </Box>
  );
};

const SCORE_RANGES = [
  { min: 0, max: 1.5, color: '#10b981', key: 'safe' },
  { min: 1.5, max: 5.5, color: '#f59e0b', key: 'watch' },
  { min: 5.5, max: 7.5, color: '#f97316', key: 'danger' },
  { min: 7.5, max: 14, color: '#ef4444', key: 'critical' },
];

const scoreTooltip = (score, t) => {
  const range = SCORE_RANGES.find((item) => score < item.max) || SCORE_RANGES[SCORE_RANGES.length - 1];
  return t(`distribution.scoreGuide.${range.key}`);
};

export const ScoreBar = ({ score }) => {
  const { t } = useTranslation();
  const range = SCORE_RANGES.find((item) => score < item.max) || SCORE_RANGES[SCORE_RANGES.length - 1];
  const color = range.color;

  return (
    <Tooltip title={scoreTooltip(score, t)} arrow placement="left">
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 0.2, minWidth: 120 }}>
        <Typography sx={{ fontSize: '0.68rem', fontWeight: 800, color, lineHeight: 1.1 }}>
          {t(`distribution.scoreGuide.${range.key}`)}
        </Typography>
      </Box>
    </Tooltip>
  );
};

export const EmptyState = ({ title, subtitle }) => (
  <Box sx={{ py: 5, px: 3, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5 }}>
    <Box sx={{ width: 56, height: 56, borderRadius: '50%', bgcolor: '#f8fafc', border: '1.5px dashed #e2e8f0', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <Assessment sx={{ fontSize: 24, color: '#cbd5e1' }} />
    </Box>
    <Box sx={{ textAlign: 'center' }}>
      <Typography sx={{ fontSize: '0.84rem', fontWeight: 600, color: '#94a3b8' }}>{title}</Typography>
      {subtitle && <Typography sx={{ fontSize: '0.72rem', color: '#cbd5e1', mt: 0.3 }}>{subtitle}</Typography>}
    </Box>
  </Box>
);

export const VNIndexSkeleton = () => (
  <Box sx={{ mb: 2 }}>
    <Box sx={{ borderRadius: 2, border: '1px solid #e2e8f0', bgcolor: '#fff', overflow: 'hidden' }}>
      <Box sx={{ px: 2, py: 1, display: 'flex', alignItems: 'center', gap: 1.5, bgcolor: '#fafbfc' }}>
        <Skeleton variant="circular" width={18} height={18} />
        <Skeleton variant="text" width={70} height={22} />
        <Skeleton variant="rounded" width={64} height={24} sx={{ borderRadius: 3 }} />
        <Skeleton variant="text" width={80} height={18} />
        <Skeleton variant="text" width={36} height={28} sx={{ fontWeight: 900 }} />
        <Box sx={{ width: '1px', height: 22, bgcolor: '#e2e8f0' }} />
        <Skeleton variant="text" width={72} height={18} />
        <Skeleton variant="text" width={64} height={18} />
        <Skeleton variant="text" width={56} height={18} />
        <Skeleton variant="text" width={72} height={18} />
        <Box sx={{ width: '1px', height: 22, bgcolor: '#e2e8f0' }} />
        <Skeleton variant="text" width={42} height={18} />
        <Skeleton variant="text" width={42} height={18} />
        <Skeleton variant="text" width={42} height={18} />
        <Box sx={{ flex: 1 }} />
        <Skeleton variant="text" width={72} height={16} />
      </Box>
      <Box sx={{ p: 1.5, display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 240px' }, gap: 1.5 }}>
        <Box sx={{ borderRadius: 1.5, bgcolor: '#f8fafc', border: '1px solid #e2e8f0', p: 1.5 }}>
          <Skeleton variant="text" width={140} height={14} sx={{ mb: 0.5 }} />
          <Box sx={{ height: 180, display: 'flex', alignItems: 'flex-end', gap: 0.5, pt: 2 }}>
            {[60, 45, 70, 85, 55, 90, 65, 40, 75, 50, 80, 60, 35, 70, 45, 55, 65, 50, 40, 55].map((height, index) => (
              <Skeleton key={index} variant="rectangular" width="100%" height={`${height}%`} sx={{ borderRadius: 0.5, flex: 1 }} />
            ))}
          </Box>
        </Box>
        <Box sx={{ borderRadius: 1.5, bgcolor: '#f8fafc', border: '1px solid #e2e8f0', p: 1.5, display: 'flex', flexDirection: 'column' }}>
          <Skeleton variant="text" width={130} height={14} sx={{ mb: 1 }} />
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1px', bgcolor: '#e2e8f0', borderRadius: 1, overflow: 'hidden', mb: 1.5 }}>
            {[0, 1].map((index) => (
              <Box key={index} sx={{ bgcolor: '#fff', textAlign: 'center', py: 0.8 }}>
                <Skeleton variant="text" width={40} height={22} sx={{ mx: 'auto' }} />
                <Skeleton variant="text" width={60} height={10} sx={{ mx: 'auto', mt: 0.3 }} />
              </Box>
            ))}
          </Box>
          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 1, justifyContent: 'center' }}>
            {['#10b981', '#f59e0b', '#f97316', '#ef4444'].map((color) => (
              <Box key={color}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.3 }}>
                  <Skeleton variant="text" width={50} height={14} />
                  <Skeleton variant="text" width={40} height={14} />
                </Box>
                <Skeleton variant="rectangular" height={6} sx={{ borderRadius: 0.5, bgcolor: alpha(color, 0.12) }} />
              </Box>
            ))}
          </Box>
        </Box>
      </Box>
    </Box>
  </Box>
);

export const TilesSkeleton = () => (
  <Box sx={{ mb: 2 }}>
    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(4, 1fr)' }, gap: 1.5 }}>
      {['#10b981', '#f59e0b', '#f97316', '#ef4444'].map((color) => (
        <Box key={color} sx={{ borderRadius: 1.5, border: `1px solid ${alpha(color, 0.15)}`, bgcolor: '#fff', p: '14px 16px' }}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
            <Skeleton variant="rounded" width={38} height={38} sx={{ borderRadius: 2, bgcolor: alpha(color, 0.15) }} />
            <Box sx={{ flex: 1 }}>
              <Skeleton variant="text" width={60} height={12} sx={{ mb: 0.5 }} />
              <Skeleton variant="text" width={40} height={32} />
              <Skeleton variant="text" width={50} height={12} sx={{ mt: 0.4 }} />
            </Box>
          </Box>
        </Box>
      ))}
    </Box>
  </Box>
);

export const TableSkeleton = () => {
  const { t } = useTranslation();
  return (
    <Box>
      <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <Box sx={{ display: 'flex', gap: 0 }}>
          {[70, 65, 65, 60, 60].map((width, index) => (
            <Skeleton key={index} variant="rectangular" width={width} height={32} sx={{ borderRadius: index === 0 ? '4px 0 0 4px' : index === 4 ? '0 4px 4px 0' : 0 }} />
          ))}
        </Box>
        <Skeleton variant="rounded" width={160} height={34} sx={{ borderRadius: 1.5 }} />
        <Box sx={{ flex: 1 }} />
        <Skeleton variant="text" width={80} height={16} />
      </Box>
      <Box sx={{ bgcolor: '#fff', borderRadius: 2, border: '1px solid #e2e8f0', overflow: 'hidden' }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              {['#', t('distribution.table.ticker'), t('distribution.table.score'), t('distribution.table.level'), t('distribution.table.action'), t('distribution.table.signals'), t('distribution.table.signalList'), t('distribution.table.pctChange'), t('distribution.table.volRatio'), t('distribution.table.rs5d')].map((header) => (
                <TableCell key={header} sx={thSx}>{header}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {[...Array(12)].map((_, index) => (
              <TableRow key={index} sx={{ '&:nth-of-type(even)': { bgcolor: '#fafbfc' }, borderLeft: '3px solid #e2e8f0' }}>
                <TableCell sx={tdSx}><Skeleton variant="text" width={16} /></TableCell>
                <TableCell sx={tdSx}><Skeleton variant="text" width={40} height={18} sx={{ fontWeight: 800 }} /></TableCell>
                <TableCell sx={tdSx}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                    <Skeleton variant="rectangular" height={6} sx={{ flex: 1, borderRadius: 1 }} />
                    <Skeleton variant="text" width={28} />
                  </Box>
                </TableCell>
                <TableCell sx={tdSx}><Skeleton variant="rounded" width={64} height={24} sx={{ borderRadius: 3 }} /></TableCell>
                <TableCell sx={tdSx}><Skeleton variant="text" width={48} /></TableCell>
                <TableCell sx={tdSx} align="center"><Skeleton variant="text" width={16} sx={{ mx: 'auto' }} /></TableCell>
                <TableCell sx={tdSx}>
                  <Box sx={{ display: 'flex', gap: 0.4 }}>
                    {[...Array(Math.floor(Math.random() * 3) + 1)].map((_, chipIndex) => (
                      <Skeleton key={chipIndex} variant="rounded" width={55 + chipIndex * 10} height={18} sx={{ borderRadius: 2 }} />
                    ))}
                  </Box>
                </TableCell>
                <TableCell sx={tdSx} align="right"><Skeleton variant="text" width={44} sx={{ ml: 'auto' }} /></TableCell>
                <TableCell sx={tdSx} align="right"><Skeleton variant="text" width={32} sx={{ ml: 'auto' }} /></TableCell>
                <TableCell sx={tdSx} align="right"><Skeleton variant="text" width={44} sx={{ ml: 'auto' }} /></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Box>
    </Box>
  );
};

export const SectorRegimeSkeleton = () => (
  <Box sx={{ mb: 2 }}>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
      <Skeleton variant="text" width={120} height={20} />
      <Skeleton variant="text" width={100} height={16} />
    </Box>
    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2,1fr)', sm: 'repeat(3,1fr)', md: 'repeat(4,1fr)', lg: 'repeat(5,1fr)' }, gap: 1 }}>
      {[...Array(10)].map((_, index) => (
        <Box key={index} sx={{ borderRadius: 1.5, border: '1px solid #e2e8f0', bgcolor: '#fff', p: '10px 12px' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.6 }}>
            <Skeleton variant="text" width={80} height={16} />
            <Skeleton variant="rounded" width={55} height={18} sx={{ borderRadius: 2 }} />
          </Box>
          <Skeleton variant="text" width={40} height={24} sx={{ mb: 0.5 }} />
          <Skeleton variant="rectangular" height={4} sx={{ borderRadius: 0.5, mb: 0.4 }} />
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Skeleton variant="text" width={50} height={12} />
            <Skeleton variant="text" width={35} height={12} />
          </Box>
        </Box>
      ))}
    </Box>
  </Box>
);

export const SectorCard = ({ sector, onClick }) => {
  const { t, i18n } = useTranslation();
  const regime = sector?.sector_regime || 'neutral';
  const color = REGIME_COLORS[regime] || '#64748b';
  const name = getSectorLabel(sector, i18n.language);

  return (
    <Box onClick={() => onClick(sector)} sx={{
      borderRadius: 1.5, border: `1px solid ${alpha(color, 0.2)}`,
      bgcolor: '#fff', p: '10px 12px', cursor: 'pointer',
      transition: 'all 0.2s',
      '&:hover': { borderColor: alpha(color, 0.4), boxShadow: `0 3px 12px ${alpha(color, 0.1)}`, transform: 'translateY(-1px)' },
    }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.6 }}>
        <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: '#1e293b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>
          {name || sector.sector}
        </Typography>
        <Chip label={t(`distribution.sectorRegime.regimes.${regime}`)} size="small" sx={{
          fontSize: '0.55rem', height: 18, fontWeight: 700, ml: 0.5,
          bgcolor: alpha(color, 0.1), color,
        }} />
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5, mb: 0.5 }}>
        <Typography sx={{ fontSize: '1.1rem', fontWeight: 900, color, fontFamily: 'monospace', lineHeight: 1 }}>
          {sector.sector_score != null ? sector.sector_score.toFixed(0) : '—'}
        </Typography>
        <Typography sx={{ fontSize: '0.55rem', color: '#94a3b8', fontWeight: 600 }}>
          {t('distribution.sectorRegime.score')}
        </Typography>
      </Box>
      <Box sx={{ mb: 0.4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.2 }}>
          <Typography sx={{ fontSize: '0.55rem', color: '#94a3b8' }}>{t('distribution.sectorRegime.aboveMa20')}</Typography>
          <Typography sx={{ fontSize: '0.58rem', fontWeight: 700, color, fontFamily: 'monospace' }}>
            {sector.pct_above_ma20 != null ? `${(sector.pct_above_ma20 * 100).toFixed(0)}%` : '—'}
          </Typography>
        </Box>
        <Box sx={{ height: 4, borderRadius: 0.5, bgcolor: alpha(color, 0.1), overflow: 'hidden' }}>
          <Box sx={{ width: `${(sector.pct_above_ma20 || 0) * 100}%`, height: '100%', borderRadius: 0.5, bgcolor: color, transition: 'width 0.3s' }} />
        </Box>
      </Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.4 }}>
        <Typography sx={{ fontSize: '0.55rem', color: '#94a3b8' }}>
          {sector.sector_size || 0} {t('distribution.sectorRegime.members').toLowerCase()}
        </Typography>
        <Typography sx={{ fontSize: '0.55rem', fontWeight: 600, fontFamily: 'monospace', color: (sector.avg_ret_5 || 0) < 0 ? '#ef4444' : '#10b981' }}>
          {sector.avg_ret_5 != null ? fmtPct(sector.avg_ret_5) : '—'}
        </Typography>
      </Box>
    </Box>
  );
};